import _ from "lodash";
import queryString from "query-string";
import styled from "styled-components";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Icon } from "semantic-ui-react";
import { useParams } from "react-router-dom";
import { useQuery } from "react-query";
import { useClient } from "../../hooks/use-client";
import { useCrmRequests, CrmStreamTable } from "./hooks/hook";
import { fetchSearchCrmRequests } from "../../queries/search";
import { StreamTableMap } from "../StreamTableMap";
import { DateTimeBadge, DateBadge, DateDueBadge } from "../badges";
import networkSelectors from "../../selectors/network";
import crmRequestsSelectors from "../../selectors/crm_requests";

const Placeholder = styled.div`
  float: right;
  width: 75px;
  height: 75px;
  margin-right: 5px;
  border-radius: 5px;
`;

const PersonBadge = styled.div`
  display: inline-block;
  overflow: hidden;
`;

const BorderlessLabel = styled.span`
  font-size: 0.7em;
  margin-right: 5px;

  i.icon {
    margin: 0 !important;
    padding: 0 !important;
  }
`;
export const CrmMap = () => {
  const { site } = useParams<{ site: string }>();
  const { clientId } = useClient(site);
  const [initialQuery, setInitialQuery] = useState<any>();
  document.title = "Map - Citizen Request Management";

  const networkHeaders = useSelector(networkSelectors.defaultHeaders);
  const crmRequestImages = useSelector(crmRequestsSelectors.getCrmRequestImages);
  const CrmRequests = useCrmRequests("crm.map", site);

  const onMapMove = useCallback(
    (visiblePins: Array<any>) => {
      CrmRequests.omnifilters.setFilterState(
        Object.assign({}, CrmRequests.omnifilters.filterState, { id: visiblePins }),
      );
    },
    [CrmRequests.omnifilters.filterState],
  );

  const pinsQueryParams = useMemo(
    () => ({
      size: 100,
      cityId: clientId,
      fields: ["locationPoint"],
      ...initialQuery,
    }),
    [clientId, initialQuery],
  );
  const pinsQuery = useQuery(["crmRequestPins", pinsQueryParams], fetchSearchCrmRequests(networkHeaders), {
    enabled: !!clientId,
  });
  const cellRenderer = useCallback((selectedPin: any, data: any) => {
    const caseId = data.row.original.caseId;
    const hasAttachment = data.row.original.hasAttachment !== 0;
    const isAttachmentImage = crmRequestImages[caseId] !== null;
    const hasPreview = hasAttachment && isAttachmentImage;
    const dateClosed = data.row.original.dateClosed;
    return (
      <div style={{ height: "100%" }}>
        {hasPreview && (
          <Placeholder
            style={{
              background: `url(${crmRequestImages[caseId]}) no-repeat center`,
              backgroundSize: "cover",
            }}
          />
        )}
        <div>
          <h4 style={{ margin: 0 }}>
            <BorderlessLabel>
              <Icon name='hashtag' color='grey' />
              {data.row.original.displayId}
            </BorderlessLabel>
            <span style={{ color: "#F26C21", marginBottom: 3 }}>{data.row.original.topicName}</span>
          </h4>
          <div style={{ width: hasAttachment ? "calc(100% - 88px)" : "100%" }}>
            <p style={{ display: "flex", margin: 0, marginBottom: "5px" }}>
              <PersonBadge>
                <Icon name='clipboard' color='grey' />
                {data.row.original.contactDisplayName}
              </PersonBadge>
              <PersonBadge>
                <Icon name='user' color='grey' style={{ marginLeft: 3 }} />
                {data.row.original.assignedToDisplayName}
              </PersonBadge>
            </p>
            <p>
              {dateClosed && <DateBadge value={data.row.original.dateExpectClose} />}
              {!dateClosed && <DateDueBadge oneLine value={data.row.original.dateExpectClose} />}
              <DateTimeBadge oneline value={data.row.original.dateEntered} />
            </p>
          </div>
        </div>
      </div>
    );
  }, []);
  const initialState = { sortBy: [{ id: CrmRequests.sort.field, desc: CrmRequests.sort.direction === "desc" }] };

  // Update view from URL
  useEffect(() => {
    const query = queryString.parse(location.search, { arrayFormat: "bracket", parseBooleans: true });
    CrmRequests.omnifilters.setFilterState(Object.assign({}, query));
  }, [CrmRequests.omnifilters.setFilterState]);

  // Capture initial query
  useEffect(() => {
    if (!initialQuery && !_.isEmpty(CrmRequests.omnifilters.query)) {
      setInitialQuery(Object.assign({}, CrmRequests.omnifilters.query));
    }
  }, [CrmRequests.omnifilters.query]);

  return (
    <StreamTableMap
      pinsQuery={pinsQuery}
      headerRenderer={(listPins: number, totalPins: number) => `Requests (Showing ${listPins} of ${totalPins})`}
      cellRenderer={cellRenderer}
      locationAttribute='_source.locationPoint'
      totalHits={CrmRequests.streamQuery.totalHits?.value}
      rows={CrmRequests.rows}
      tableRenderer={({
        tableProps,
        containerProps,
        columns,
      }: {
        tableProps: any;
        containerProps: any;
        columns: Array<any>;
      }) => (
        <CrmStreamTable
          {...CrmRequests.streamQuery}
          sortBy={CrmRequests.sort.field}
          sortDirection={CrmRequests.sort.direction}
          columns={columns}
          rows={CrmRequests.rows}
          initialState={initialState}
          containerProps={containerProps}
          tableProps={tableProps}
        />
      )}
      onMapMove={onMapMove}
    />
  );
};
